// ── THE ORDER JULIAN PUT THEM IN ─────────────────────────────────────────
//
// A client's estimates were sorted newest first. Julie Tait's four were all
// written on the same day, so the sort had nothing to tell them apart and
// fell back to whatever order the server happened to return. Open one, come
// back, and the list had rearranged itself under him.
//
// Two rules fix that, and they are the whole of this file.
//
//   NOTHING MOVES ON ITS OWN. Equal dates are broken by the record's own id,
//   which never changes. A list nobody has touched reads the same way every
//   time it is drawn, on any device, forever.
//
//   WHAT HE DRAGS, STAYS. Dragging writes a position onto every row at once,
//   so a hand-ordered list is ordered entirely by hand. There is no half
//   state where three rows obey him and the fourth obeys a date.

// Newest first, and the id settles a tie. A Mongo id begins with the second
// it was created in, so two estimates written on the same day still come out
// in the order they were written rather than in the order they arrived.
function byDateThenId(a, b) {
  const at = Date.parse(a?.estimate?.submittedAt || a?.createdAt || 0) || 0;
  const bt = Date.parse(b?.estimate?.submittedAt || b?.createdAt || 0) || 0;
  if (bt !== at) return bt - at;
  return String(a?._id || '').localeCompare(String(b?._id || ''));
}

const placed = (r) => Number.isFinite(Number(r?.listOrder));

/**
 * A client's estimates, in the order they should be drawn.
 *
 * Hand-placed rows lead, in his order. Anything without a position follows,
 * newest first — which is every row until the first time he drags one, and
 * the new estimate he raises tomorrow after that.
 */
export function orderEstimates(estimates) {
  const list = (Array.isArray(estimates) ? estimates : []).filter(Boolean);
  const byHand = list.filter(placed).sort((a, b) => Number(a.listOrder) - Number(b.listOrder));
  const rest = list.filter((r) => !placed(r)).sort(byDateThenId);
  return [...byHand, ...rest];
}

/**
 * Move the row at `from` to `to`, and return the ids in their new order.
 *
 * The caller sends the whole list back to the server rather than one changed
 * row: positions are only meaningful against each other, and writing one of
 * them leaves the rest describing an order that no longer exists.
 */
export function moveTo(estimates, from, to) {
  const list = orderEstimates(estimates);
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return list.map((r) => String(r._id));
  }
  const next = [...list];
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next.map((r) => String(r._id));
}
